/* eslint-disable no-underscore-dangle */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Line } from "react-chartjs-2";
import _ from "lodash";
import Chart from "chart.js";
import annotation from "chartjs-plugin-annotation";
import { getROI, nFormatter } from "lib/utils";
import { makeStyles, useMediaQuery, Typography } from "@material-ui/core";
import { BsArrowUp } from "react-icons/bs";
import CustomTooltip from "app/common/chart/CustomTooltipComponent";
import ConfirmationDialog from "app/common/dialog/ConfirmationDialog";
import ScenarioTotalsCardComponent from "app/scenarios/map/ScenarioTotalsCardComponent";
import { PriceOut, SellOut } from "resources/constants";
import Cookies from "universal-cookie";
// import { getEstimationElasticity } from "lib/utils/estimations";

Chart.plugins.register(annotation);

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    height: "100%",
    width: "100%",
  },
  priceTooltipContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  priceTooltipInfo: {
    width: "100px",
    alignItems: "center",
    display: "flex",
  },
  spaceBetweenPrice: {
    marginRight: "3px",
    fontSize: "13px",
  },
  labelPriceTooltip: {
    marginBottom: "1px",
  },
  percentageColor: {
    color: "#a2a2a2",
    fontSize: "13px",
  },
  totalItemPercentage: {
    display: "flex",
    flexDirection: "row",
    padding: "0px 10px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
    },
  },
}));

const cookies = new Cookies();

export default function ElasticityCurveChartComponent({
  selectedRow,
  setSelectedPrice,
  isReference,
  selectedRowByDate,
  selectedReferenceRowByDate,
  setIsMonthLevelChange,
  isTradePromotionAnnual,
  // eslint-disable-next-line
  groupBy,
  tupleEstimations,
  referenceRow,
}) {
  const classes = useStyles();
  const chartRef = useRef(null);
  const wrapperRef = useRef(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [tooltipProps, setTooltipProps] = useState({});
  const [chartData, setChartData] = useState({});
  const metadataSlice = useSelector((state) => state.metadata);
  const [clickedPrice, setClickedPrice] = useState(
    selectedRowByDate ? selectedRowByDate.price : selectedRow.price
  );
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const scenariosSlice = useSelector((state) => state.scenarios);
  const user = useSelector((state) => state.auth.user);
  const [sortedEstimations, setSortedEstimations] = useState(
    _.sortBy(tupleEstimations, (estimation) => estimation.price)
  );
  const [isPriceHover, setIsPriceHover] = useState(false);
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const sellOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? SellOut
    : "Sell Out";
  const priceOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? PriceOut
    : "Price Out";

  const isTpo = !window.location.pathname.includes("pricing");

  const setPositionAndData = (payload) => {
    setTooltipProps({
      top: payload.top,
      left: payload.left,
      position: "absolute",
      tooltipModel: payload.tooltipModel,
      showTooltip: true,
      width: "220px",
    });
  };

  const hide = () => {
    setTooltipProps({
      ...tooltipProps,
      showTooltip: false,
    });
  };

  const updateChartData = (price) => {
    const chart = chartRef.current.chartInstance;

    if (_.isEmpty(chart.data.datasets)) return;

    const pointRadiusArray = _.map(sortedEstimations, (estimation) => {
      if (price === estimation.price) return 5;
      return 3;
    });

    chart.data.datasets[0].pointRadius = [...pointRadiusArray];

    chart.data.datasets[1].pointRadius = [...pointRadiusArray];

    const referenceByDateOrAggregated = selectedReferenceRowByDate
      ? selectedReferenceRowByDate.price
      : referenceRow.price;

    chart.options.annotation = {
      annotations: [
        {
          drawTime: "afterDraw",
          type: "line",
          mode: "vertical",
          scaleID: "Price",
          value: isReference ? price : referenceByDateOrAggregated,
          borderColor: "#CCCCCC",
          borderWidth: 2,
          borderDash: [4, 4],
          borderDashOffset: 5,
        },
        {
          drawTime: "afterDraw",
          type: "line",
          mode: "vertical",
          scaleID: "Price",
          value: price,
          borderColor: "#F8AB10",
          borderWidth: 2,
          borderDash: [4, 4],
          borderDashOffset: 5,
        },
      ],
    };

    chart.update();
  };

  const updatePrice = (price) => {
    updateChartData(price);
    // eslint-disable-next-line no-unused-expressions
    setIsMonthLevelChange !== null ? setIsMonthLevelChange(false) : null;
    setSelectedPrice(price);
  };

  const onConfirmAction = (e, confirm) => {
    e.stopPropagation();
    if (checkboxChecked) {
      cookies.set(
        "cycleIdByUserSelected",
        JSON.stringify({
          ...cookies.get("cycleIdByUserSelected"),
          [`${user.username}-${scenariosSlice.selectedScenario.scenario.cycle.id}`]: true,
        }),
        { path: "/" }
      );
    }
    if (confirm) {
      updatePrice(clickedPrice);
    }
    setConfirmDialogOpen(false);
    setCheckboxChecked(false);
  };

  const chartDataUpdate = (
    auxSortedEstimations,
    pointRadiusArray,
    auxSortedEstimationsWithReference
  ) => {
    return {
      labels: _.map(
        !isReference ? auxSortedEstimationsWithReference : auxSortedEstimations,
        (estimation) => estimation.price
      ),
      datasets: [
        {
          label: sellOutLabelByTenant,
          yAxisID: "units",
          fill: false,
          borderColor: "#47B5DB",
          pointBackgroundColor: "white",
          pointHoverRadius: 5,
          pointHoverBorderWidth: 2,
          hoverBackgroundColor: "white",
          pointHitRadius: 5,
          pointRadius: pointRadiusArray,
          data: _.map(auxSortedEstimations, (estimation) => {
            return {
              ...estimation,
              title: `Price: ${estimation.price}`,
              borderStyle: { borderColor: "#181E2A" },
              x: estimation.price,
              y: estimation.units,
              units: {
                absolute: estimation.units,
                percentage:
                  (100 *
                    (estimation.units -
                      (selectedReferenceRowByDate
                        ? selectedReferenceRowByDate.units
                        : referenceRow.units))) /
                  Math.abs(
                    selectedReferenceRowByDate
                      ? selectedReferenceRowByDate.units
                      : referenceRow.units
                  ),
              },
              units_in: {
                absolute: estimation.units_in,
                percentage:
                  (100 *
                    (estimation.units_in -
                      (selectedReferenceRowByDate
                        ? selectedReferenceRowByDate.units_in
                        : referenceRow.units_in))) /
                  Math.abs(
                    selectedReferenceRowByDate
                      ? selectedReferenceRowByDate.units_in
                      : referenceRow.units_in
                  ),
              },
              margin: {
                absolute: estimation.margin,
                percentage:
                  (100 *
                    (estimation.margin -
                      (selectedReferenceRowByDate
                        ? selectedReferenceRowByDate.margin
                        : referenceRow.margin))) /
                  Math.abs(
                    selectedReferenceRowByDate
                      ? selectedReferenceRowByDate.margin
                      : referenceRow.margin
                  ),
              },
              sales: {
                absolute: estimation.sales,
                percentage:
                  (100 *
                    (estimation.sales -
                      (selectedReferenceRowByDate
                        ? selectedReferenceRowByDate.sales
                        : referenceRow.sales))) /
                  Math.abs(
                    selectedReferenceRowByDate
                      ? selectedReferenceRowByDate.sales
                      : referenceRow.sales
                  ),
              },
            };
          }),
        },
        {
          label: "Margin",
          yAxisID: "Margin",
          fill: false,
          borderColor: "#66AE91",
          pointBackgroundColor: "white",
          pointHoverRadius: 5,
          pointHoverBorderWidth: 2,
          hoverBackgroundColor: "white",
          pointHitRadius: 5,
          pointRadius: pointRadiusArray,
          data: _.map(auxSortedEstimations, (estimation) => {
            return {
              ...estimation,
              title: `Price: ${estimation.price}`,
              color: "#545455",
              x: estimation.price,
              y: estimation.margin,
              units: {
                absolute: estimation.units,
                percentage:
                  (100 *
                    (estimation.units -
                      (selectedReferenceRowByDate
                        ? selectedReferenceRowByDate.units
                        : referenceRow.units))) /
                  Math.abs(
                    selectedReferenceRowByDate
                      ? selectedReferenceRowByDate.units
                      : referenceRow.units
                  ),
              },
              units_in: {
                absolute: estimation.units_in,
                percentage:
                  (100 *
                    (estimation.units_in -
                      (selectedReferenceRowByDate
                        ? selectedReferenceRowByDate.units_in
                        : referenceRow.units_in))) /
                  Math.abs(
                    selectedReferenceRowByDate
                      ? selectedReferenceRowByDate.units_in
                      : referenceRow.units_in
                  ),
              },
              margin: {
                absolute: estimation.margin,
                percentage:
                  (100 *
                    (estimation.margin -
                      (selectedReferenceRowByDate
                        ? selectedReferenceRowByDate.margin
                        : referenceRow.margin))) /
                  Math.abs(
                    selectedReferenceRowByDate
                      ? selectedReferenceRowByDate.margin
                      : referenceRow.margin
                  ),
              },
              sales: {
                absolute: estimation.sales,
                percentage:
                  (100 *
                    (estimation.sales -
                      (selectedReferenceRowByDate
                        ? selectedReferenceRowByDate.sales
                        : referenceRow.sales))) /
                  Math.abs(
                    selectedReferenceRowByDate
                      ? selectedReferenceRowByDate.sales
                      : referenceRow.sales
                  ),
              },
            };
          }),
        },
      ],
    };
  };

  const handlePriceClick = (price) => {
    const isCycleIgnoredByUser = cookies.get("cycleIdByUserSelected")?.[
      `${user.username}-${scenariosSlice.selectedScenario.scenario.cycle.id}`
    ];
    setClickedPrice(price);
    if (isReference && !isCycleIgnoredByUser) setConfirmDialogOpen(true);
    else updatePrice(price);
  };

  useEffect(() => {
    let isSelectedReferenceRowPriceInSortedEstimations;
    // When we select a pricing strategy by product, product-retailer or product-area the price of the row is a weighted average and may not coincide with any of the price points retrieved from the server. That is why we have to append it to the tupleEstimations if a match is not found
    const isSelectedRowPriceInSortedEstimations = _.find(
      sortedEstimations,
      (estimation) =>
        estimation.price ===
        (selectedRowByDate ? selectedRowByDate.price : selectedRow.price)
    );

    const auxSortedEstimations = !isSelectedRowPriceInSortedEstimations
      ? _.sortBy(
          [selectedRowByDate || selectedRow, ...tupleEstimations],
          (estimation) => estimation.price
        )
      : _.sortBy(
          [
            ..._.filter(tupleEstimations, (estimation) => {
              return (
                estimation.price !==
                (selectedRowByDate
                  ? selectedRowByDate.price
                  : selectedRow.price)
              );
            }),
            selectedRowByDate || selectedRow,
          ],
          (estimation) => estimation.price
        );

    let auxSortedEstimationsWithReference = null;

    if (!isReference) {
      isSelectedReferenceRowPriceInSortedEstimations = _.find(
        auxSortedEstimations,
        (estimation) =>
          estimation.price ===
          (selectedReferenceRowByDate
            ? selectedReferenceRowByDate.price
            : referenceRow.price)
      );
      const localReferenceRow = selectedReferenceRowByDate || referenceRow;
      auxSortedEstimationsWithReference = !isSelectedReferenceRowPriceInSortedEstimations
        ? _.sortBy(
            [
              localReferenceRow.price !== selectedRow.price
                ? localReferenceRow
                : selectedRow,
              ...auxSortedEstimations,
            ],
            (estimation) => estimation.price
          )
        : _.sortBy(
            [
              ..._.filter(auxSortedEstimations, (estimation) => {
                return estimation.price !== localReferenceRow.price;
              }),
              localReferenceRow.price !== selectedRow.price
                ? localReferenceRow
                : selectedRow,
            ],
            (estimation) => estimation.price
          );
    }

    setSortedEstimations(auxSortedEstimations);

    const pointRadiusArray = _.map(auxSortedEstimations, (estimation) => {
      if (
        selectedRowByDate !== null
          ? selectedRowByDate.price
          : selectedRow.price === estimation.price
      )
        return 5;
      return 3;
    });

    setChartData(
      chartDataUpdate(
        auxSortedEstimations,
        pointRadiusArray,
        auxSortedEstimationsWithReference
      )
    );
  }, [tupleEstimations, selectedRow.price]);

  useEffect(() => {
    updateChartData(
      selectedRowByDate ? selectedRowByDate.price : selectedRow.price
    );
  }, [referenceRow, chartData]);

  useEffect(() => {
    const chart = chartRef.current.chartInstance;
    if (_.isEmpty(chart.data.datasets)) return;

    const pointRadiusArray = _.map(sortedEstimations, (estimation) => {
      if (
        (selectedRowByDate ? selectedRowByDate.price : selectedRow.price) ===
        estimation.price
      )
        return 5;
      return 3;
    });

    chart.data.datasets[0].pointRadius = [...pointRadiusArray];

    chart.data.datasets[1].pointRadius = [...pointRadiusArray];

    chart.update();
  }, [sortedEstimations, referenceRow]);

  const chartOptions = {
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    // legend: {
    //   align: "start",
    //   rtl: false,
    //   labels: {
    //     color: "rgb(255, 99, 132)",
    //   },
    // },
    layout: {
      padding: {
        left: 50,
        right: 70,
        top: 20,
        bottom: 20,
      },
    },
    animation: false,
    tooltips: {
      enabled: false,
      intersect: isSmallScreen,
      mode: "index",
      custom: useCallback((tooltipModel) => {
        if (tooltipModel.opacity === 0) {
          hide();
          return;
        }

        const chart = chartRef.current.chartInstance;
        const { canvas } = chart.ctx;

        if (canvas) {
          // set position of tooltip

          const valueTopTpo = isTradePromotionAnnual ? 100 : 0;

          const left =
            tooltipModel.caretX <= chart.width / 2
              ? tooltipModel.caretX + 60
              : tooltipModel.caretX - 280;
          const top = tooltipModel.caretY + valueTopTpo;

          setPositionAndData({ top, left, tooltipModel });
        }
      }, []),
    },
    onHover: useCallback((event, chartElement) => {
      const newEvent = event;
      if (!chartElement[0]) newEvent.target.style.cursor = "default";
      else newEvent.target.style.cursor = "pointer";
      const chart = chartRef.current.chartInstance;
      const datasetIndex = 0;
      const activeTooltip = chart.tooltip._active
        ? chart.tooltip._active[datasetIndex]
        : null;
      if (activeTooltip) {
        const selectedPrice =
          chart.data.datasets[datasetIndex].data[
            activeTooltip._index >
            chart.data.datasets[datasetIndex].data.length - 1
              ? activeTooltip._index - 1
              : activeTooltip._index
          ].price;
        const isPrice = _.filter(sortedEstimations, (estimations) => {
          return estimations.price === selectedPrice;
        });

        if (isPrice.length === 0 && selectedPrice) {
          setIsPriceHover(true);
          return;
        }
        setIsPriceHover(false);
      }
    }, []),
    onClick: configCatSlice?.features?.IsMaintenanceActive
      ? undefined
      : useCallback((evt) => {
          const chart = chartRef.current.chartInstance;
          const datasetIndex = 0;
          // If chart isnt active when price change do noting
          // because we dont know what point was selected
          if (!chart.tooltip._active) return;
          const activeTooltip = chart.tooltip._active[datasetIndex];
          if (!activeTooltip) return;
          const selectedPrice =
            chart.data.datasets[datasetIndex].data[activeTooltip._index].price;

          const isPrice = _.filter(sortedEstimations, (estimations) => {
            return estimations.price === selectedPrice;
          });

          if (isPrice.length === 0) {
            setIsPriceHover(true);
            return;
          }
          setIsPriceHover(false);
          /**
           * To improve usability, we first take the price contained in the tooltip
           * shown to the user, so that it does not select a price that the user is
           * not aware of
           */
          if (activeTooltip) {
            handlePriceClick(
              chart.data.datasets[datasetIndex].data[activeTooltip._index].price
            );
          } else {
            // No tooltip active, get data from clicked data point
            const element = chart.getElementAtEvent(evt);

            if (element.length)
              handlePriceClick(
                sortedEstimations[element[datasetIndex]._index].price
              );
          }
        }, []),
    scales: {
      yAxes: [
        {
          id: "units",
          scaleLabel: {
            labelString: sellOutLabelByTenant,
            fontStyle: "bold",
            display: false,
          },
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
            color: "#A9A9A9",
            lineWidth: 2,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 5,
            maxRotation: 0,
            minRotation: 0,
            callback: React.useCallback((value) => {
              return `${nFormatter(value)}`;
            }, []),
          },
          position: "left",
        },
        {
          id: "Margin",
          // scaleLabel: {
          //   labelString: `Margin`,
          //   fontStyle: "bold",
          //   display: true,
          // },
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
            color: "#A9A9A9",
            lineWidth: 2,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 5,
            maxRotation: 0,
            minRotation: 0,
            callback: React.useCallback((value) => {
              return `${nFormatter(value)}`;
            }, []),
          },
          position: "right",
        },
      ],
      xAxes: [
        {
          id: "Price",
          scaleLabel: {
            labelString: priceOutLabelByTenant,
            display: false,
            fontStyle: "bold",
          },
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
            color: "#F8AB10",
            lineWidth: 1,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 5,
            maxRotation: 0,
            minRotation: 0,
            callback: React.useCallback((value) => {
              return `${nFormatter(value)}`;
            }, []),
          },
          position: "bottom",
        },
        // {
        //   id: "Top price",
        //   gridLines: {
        //     drawOnChartArea: false,
        //     drawTicks: false,
        //     lineWidth: isTradePromotionAnnual ? 0 : 2,
        //   },
        //   ticks: {
        //     display: false,
        //   },
        //   position: "top",
        // },
      ],
    },
  };

  const ROI = () => {
    let ret = null;
    try {
      const rowValues =
        chartRef.current.chartInstance.data.datasets[
          tooltipProps.tooltipModel.dataPoints[0].datasetIndex
        ].data[
          tooltipProps.tooltipModel.dataPoints[0].index >
          chartRef.current.chartInstance.data.datasets[
            tooltipProps.tooltipModel.dataPoints[0].datasetIndex
          ].data.length -
            1
            ? tooltipProps.tooltipModel.dataPoints[0].index - 1
            : tooltipProps.tooltipModel.dataPoints[0].index
        ];

      const toolTipEstimation = _.minBy(tupleEstimations, (estimation) =>
        Math.abs(estimation.price - rowValues.price)
      );

      let refPriceIn = referenceRow.price_in;
      let estPriceIn = toolTipEstimation.price_in;

      if (configCatSlice?.features.newInvestmentCalculation) {
        refPriceIn = referenceRow.price_in_wo_taxes;
        estPriceIn = toolTipEstimation.price_in_wo_taxes;
      }

      const investment = (refPriceIn - estPriceIn) * toolTipEstimation.units_in;

      const correctedROI =
        rowValues?.roi !== 0 && rowValues?.roi !== undefined
          ? rowValues.roi
          : getROI(rowValues.margin.absolute, referenceRow.margin, investment);

      const titleForROIGroupBy = (aggrupation) => {
        if (aggrupation) {
          return `≈ ${correctedROI}`;
        }
        return correctedROI;
      };

      // eslint-disable-next-line no-nested-ternary
      ret =
        // eslint-disable-next-line no-nested-ternary
        isReference || !isTpo
          ? null
          : rowValues.price > referenceRow.price
          ? "N/A"
          : titleForROIGroupBy(groupBy);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error calculating ROI: ", error);
    }
    return ret;
  };

  return (
    <>
      <ConfirmationDialog
        title="Reference Change"
        content="Reference scenario will also change for your teammates. Additionally,
          all the comparisons against it will be updated. Do you want to proceed?"
        open={confirmDialogOpen}
        onConfirmAction={onConfirmAction}
        onCheckboxChecked={setCheckboxChecked}
        isCheckboxComponent
      />

      <div ref={wrapperRef} className={classes.chartContainer}>
        <Line ref={chartRef} data={chartData} options={chartOptions} />
        {tooltipProps.showTooltip ? (
          <CustomTooltip
            roi={ROI()}
            isPriceHover={isPriceHover}
            tooltipProps={tooltipProps}
            tooltipData={{ title: "" }}
            items={[
              {
                content: (
                  <div>
                    <Typography
                      variant="body2"
                      className={classes.labelPriceTooltip}
                    >
                      Price Out
                    </Typography>
                    <div className={classes.priceTooltipContainer}>
                      <div className={classes.priceTooltipInfo}>
                        <Typography
                          variant="body2"
                          className={classes.spaceBetweenPrice}
                        >
                          {metadataSlice["custom-projection"].price[0].unit}
                        </Typography>
                        <Typography variant="body2">
                          {
                            chartRef.current.chartInstance.data.datasets[
                              tooltipProps.tooltipModel.dataPoints[0]
                                .datasetIndex
                            ].data[
                              tooltipProps.tooltipModel.dataPoints[0].index >
                              chartRef.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data.length -
                                1
                                ? tooltipProps.tooltipModel.dataPoints[0]
                                    .index - 1
                                : tooltipProps.tooltipModel.dataPoints[0].index
                            ].price
                          }
                        </Typography>
                      </div>
                      {!isReference &&
                        Math.abs(
                          ((chartRef.current.chartInstance.data.datasets[
                            tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                          ].data[
                            tooltipProps.tooltipModel.dataPoints[0].index >
                            chartRef.current.chartInstance.data.datasets[
                              tooltipProps.tooltipModel.dataPoints[0]
                                .datasetIndex
                            ].data.length -
                              1
                              ? tooltipProps.tooltipModel.dataPoints[0].index -
                                1
                              : tooltipProps.tooltipModel.dataPoints[0].index
                          ].price -
                            (selectedReferenceRowByDate
                              ? selectedReferenceRowByDate.price
                              : referenceRow.price)) *
                            100) /
                            (selectedReferenceRowByDate
                              ? selectedReferenceRowByDate.price
                              : referenceRow.price)
                        )
                          .toFixed(2)
                          .toString() !== "0.00" && (
                          <div
                            component="span"
                            className={`${classes.totalItemPercentage} ${classes.percentageColor}`}
                          >
                            <BsArrowUp
                              size={20}
                              strokeWidth={1.5}
                              style={{
                                marginRight: "5px",
                                color: "#a2a2a2",
                                transform: `rotateX(${
                                  chartRef.current.chartInstance.data.datasets[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .datasetIndex
                                  ].data[
                                    tooltipProps.tooltipModel.dataPoints[0]
                                      .index >
                                    chartRef.current.chartInstance.data
                                      .datasets[
                                      tooltipProps.tooltipModel.dataPoints[0]
                                        .datasetIndex
                                    ].data.length -
                                      1
                                      ? tooltipProps.tooltipModel.dataPoints[0]
                                          .index - 1
                                      : tooltipProps.tooltipModel.dataPoints[0]
                                          .index
                                  ].price -
                                    (selectedReferenceRowByDate
                                      ? selectedReferenceRowByDate.price
                                      : referenceRow.price) >
                                  0
                                    ? "0deg"
                                    : "180deg"
                                }
                                  )`,
                              }}
                            />
                            {Math.abs(
                              ((chartRef.current.chartInstance.data.datasets[
                                tooltipProps.tooltipModel.dataPoints[0]
                                  .datasetIndex
                              ].data[
                                tooltipProps.tooltipModel.dataPoints[0].index >
                                chartRef.current.chartInstance.data.datasets[
                                  tooltipProps.tooltipModel.dataPoints[0]
                                    .datasetIndex
                                ].data.length -
                                  1
                                  ? tooltipProps.tooltipModel.dataPoints[0]
                                      .index - 1
                                  : tooltipProps.tooltipModel.dataPoints[0]
                                      .index
                              ].price -
                                (selectedReferenceRowByDate
                                  ? selectedReferenceRowByDate.price
                                  : referenceRow.price)) *
                                100) /
                                (selectedReferenceRowByDate
                                  ? selectedReferenceRowByDate.price
                                  : referenceRow.price)
                            ).toFixed(2)}
                            %
                          </div>
                        )}
                    </div>
                  </div>
                ),
                borderStyle: {
                  borderColor: "#F8AB10",
                },
              },
            ]}
            body={
              <ScenarioTotalsCardComponent
                cardData={
                  chartRef.current.chartInstance.data.datasets[
                    tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                  ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                }
                isReference={isReference}
                metadata={metadataSlice}
                visibleKeys={["units", "margin", "sales"]}
                showLabels
                showInChart
                showIcons={false}
                showLine
                orderWithVisibleKeys
              />
            }
          />
        ) : null}
      </div>
    </>
  );
}
